import { useCallback, useEffect, useState } from "react";
import { adminAutomationsStatus, adminAutomationsHistory, adminAutomationsRun } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import {
  Play, RefreshCw, CheckCircle2, XCircle, Loader2, Clock, Zap, Activity,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  success: { tint: "bg-emerald-50 border-emerald-200", pill: "bg-emerald-100 text-emerald-800", icon: <CheckCircle2 size={14} className="text-emerald-600"/> },
  error:   { tint: "bg-red-50 border-red-200", pill: "bg-red-100 text-red-800", icon: <XCircle size={14} className="text-red-600"/> },
  running: { tint: "bg-amber-50 border-amber-200", pill: "bg-amber-100 text-amber-900", icon: <Loader2 size={14} className="text-amber-600 animate-spin"/> },
  never:   { tint: "bg-stone-50 border-stone-200", pill: "bg-stone-100 text-stone-600", icon: <Clock size={14} className="text-stone-500"/> },
};

function fmtRelative(iso) {
  if (!iso) return "—";
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return iso; }
}

function fmtAbsolute(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function Automations() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);   // job_id currently running
  const [viewing, setViewing] = useState(null);   // {job_id, name} for history modal

  const load = useCallback(async (showToast = false) => {
    try {
      const r = await adminAutomationsStatus();
      setData(r);
      if (showToast) toast.success("Refreshed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load automations");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Auto-refresh every 30s so a scheduler run appears without a manual F5.
    const id = setInterval(() => load(false), 30 * 1000);
    return () => clearInterval(id);
  }, [load]);

  const runNow = async (job) => {
    if (!window.confirm(`Trigger the "${job.name}" job right now?`)) return;
    setRunning(job.id);
    try {
      const r = await adminAutomationsRun(job.id);
      toast.success(`${job.name} finished`);
      // Best-effort — show a small summary line via console for staff.
      // The Automations Health card will pick up the fresh log row on next load.
      if (r?.result) console.log(`[${job.id}]`, r.result);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${job.name} failed`);
      await load();
    } finally { setRunning(null); }
  };

  return (
    <div data-testid="admin-automations-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-wider text-ccc-purple">Operations</div>
          <h1 className="font-serif text-4xl text-ccc-plum">Automations.</h1>
          <p className="text-sm text-stone-600 mt-1">
            Scheduled jobs that run in the background. All times are Denver-local.
          </p>
        </div>
        <Button variant="outline" onClick={() => load(true)} data-testid="automations-refresh-btn">
          <RefreshCw size={14} className="mr-1"/> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-stone-500">Loading…</div>
      ) : !data?.enabled ? (
        <div className="bg-amber-50 border border-amber-300 p-6" data-testid="automations-disabled">
          <div className="font-serif text-xl text-amber-900 flex items-center gap-2">
            <XCircle size={20}/> Scheduler disabled
          </div>
          <p className="text-sm text-amber-800 mt-2">
            <code>ENABLE_AUTOMATIONS=false</code> in <code>backend/.env</code>. All scheduled jobs are dormant.
            Manual triggers via the Run-now button still work.
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {data.jobs.map(job => (
            <JobCard
              key={job.id} job={job}
              running={running === job.id}
              onRun={() => runNow(job)}
              onHistory={() => setViewing({ id: job.id, name: job.name })}
            />
          ))}
        </div>
      )}

      <HistoryDialog viewing={viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}/>
    </div>
  );
}

function JobCard({ job, running, onRun, onHistory }) {
  const status = running ? "running" : (job.last_run?.status || "never");
  const s = STATUS_STYLES[status] || STATUS_STYLES.never;
  const last = job.last_run;
  const errRate30d =
    (job.success_30d + job.error_30d) > 0
      ? Math.round((job.error_30d / (job.success_30d + job.error_30d)) * 100)
      : 0;

  return (
    <div className={`border ${s.tint} p-4`} data-testid={`automation-card-${job.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 flex items-center gap-1 ${s.pill}`}
                  data-testid={`automation-status-${job.id}`}>
              {s.icon} {status}
            </span>
            <span className="text-xs text-stone-500 font-mono">{job.id}</span>
          </div>
          <div className="font-serif text-lg text-ccc-plum">{job.name}</div>
          <div className="text-sm text-stone-600 mt-0.5">{job.description}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 mt-3 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500">Schedule</div>
              <div className="text-stone-700">{job.cron_desc}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500">Next run</div>
              <div className="text-stone-700" title={fmtAbsolute(job.next_run_at)}>
                {fmtRelative(job.next_run_at)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500">Last run</div>
              <div className="text-stone-700" title={fmtAbsolute(last?.started_at)}>
                {last ? fmtRelative(last.started_at) : "never"}
                {last?.trigger?.startsWith("manual") && (
                  <span className="text-[10px] text-ccc-purple ml-1">· manual</span>
                )}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500">30-day tally</div>
              <div className="text-stone-700">
                <span className="text-emerald-700">{job.success_30d} ok</span>
                {job.error_30d > 0 && (
                  <span className="text-red-700 ml-2">· {job.error_30d} err ({errRate30d}%)</span>
                )}
              </div>
            </div>
          </div>
          {last?.status === "error" && last?.error && (
            <div className="text-xs text-red-700 mt-2 border-l-2 border-red-300 pl-2 font-mono"
                 data-testid={`automation-error-${job.id}`}>
              {last.error}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button size="sm" onClick={onRun} disabled={running}
                  className="bg-ccc-purple hover:bg-ccc-plum text-white"
                  data-testid={`automation-run-${job.id}`}>
            {running ? <><Loader2 size={12} className="mr-1 animate-spin"/> Running…</>
                     : <><Play size={12} className="mr-1"/> Run now</>}
          </Button>
          <Button size="sm" variant="outline" onClick={onHistory}
                  data-testid={`automation-history-${job.id}`}>
            <Activity size={12} className="mr-1"/> History
          </Button>
        </div>
      </div>
    </div>
  );
}

function HistoryDialog({ viewing, onOpenChange }) {
  const [entries, setEntries] = useState(null);
  useEffect(() => {
    if (viewing) {
      adminAutomationsHistory(viewing.id, 30).then(r => setEntries(r.entries)).catch(() => setEntries([]));
    } else { setEntries(null); }
  }, [viewing]);
  if (!viewing) return null;
  return (
    <Dialog open={!!viewing} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="automation-history-dialog">
        <DialogHeader>
          <DialogTitle>{viewing.name} · run history</DialogTitle>
          <DialogDescription>Last 30 runs, newest first.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {entries === null ? (
            <div className="text-sm text-stone-500">Loading…</div>
          ) : entries.length === 0 ? (
            <div className="text-sm text-stone-500 py-6 text-center">No runs yet.</div>
          ) : (
            entries.map((e, idx) => {
              const s = STATUS_STYLES[e.status] || STATUS_STYLES.never;
              const durMs = e.finished_at && e.started_at
                ? new Date(e.finished_at) - new Date(e.started_at) : null;
              return (
                <div key={idx} className={`border ${s.tint} p-2.5 text-xs`}
                     data-testid={`history-row-${idx}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 flex items-center gap-1 ${s.pill}`}>
                      {s.icon} {e.status}
                    </span>
                    <span className="text-stone-500 font-mono">{fmtAbsolute(e.started_at)}</span>
                    {e.trigger && (
                      <span className="text-stone-500">· {e.trigger}</span>
                    )}
                    {durMs != null && (
                      <span className="text-stone-500 ml-auto">{durMs} ms</span>
                    )}
                  </div>
                  {e.error && <div className="text-red-700 font-mono">{e.error}</div>}
                  {e.summary && (
                    <pre className="text-stone-700 whitespace-pre-wrap font-mono text-[11px] leading-tight">
{JSON.stringify(e.summary, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="automation-history-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
